import DefaultLink from '@/components/common/default/DefaultLink';
import NoDataCard from '@/components/common/NoDataCard';
import { PrimaryButtonLink } from '@/components/common/PrimaryButton';
import useWishlist from '@/hooks/useWishlist';
import { Routes } from '@/types/routes.enum';
import { getSkuMainImage, SkuMainImage } from '@/utilites/get-sku-main-image';
import { toDe } from '@/utilites/sku-operations';
import { Transition } from '@headlessui/react';
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';
import { AiOutlineHeart } from 'react-icons/ai';
import HeaderWishlistDropdownSkeleton from './HeaderWishlistDropdownSkeleton';
import { CommonEnum } from '@/types/translations/CommonEnum';
import useTypedTranslation from '@/hooks/useTypedTranslation';

export default function HeaderWishlistDropdown() {
  const { wishlist, isLoadingWishlist } = useWishlist(); // Assuming isLoading is added to the hook
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const { translate: translateCommon } = useTypedTranslation('common');

  // Improved outside click and focus management
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        event.button !== 2 &&
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <nav
      ref={dropdownRef}
      className="tw-relative tw-inline-block"
      onMouseEnter={() => setIsOpen(true)}
      onMouseLeave={() => setIsOpen(false)}
    >
      <DefaultLink
        href="/dashboard/wishlist"
        className="tw-focus:outline-none tw-focus:ring-2 tw-focus:ring-offset-2 tw-focus:ring-main/30 tw-flex tw-cursor-pointer tw-items-center tw-rounded-xl tw-border tw-border-solid tw-border-gray-300 tw-p-2 tw-text-base tw-font-semibold tw-leading-6 tw-text-textBlack tw-shadow-lg hover:tw-text-main"
      >
        <AiOutlineHeart className="tw-text-2xl" />
      </DefaultLink>

      <Transition
        show={isOpen}
        enter="tw-transition tw-ease-out tw-duration-200"
        enterFrom="tw-opacity-0 tw-scale-95"
        enterTo="tw-opacity-100 tw-scale-100"
        leave="tw-transition tw-ease-in tw-duration-150"
        leaveFrom="tw-opacity-100 tw-scale-100"
        leaveTo="tw-opacity-0 tw-scale-95"
        unmount={false}
      >
        <div
          id="header-dropdown-menu"
          role="menu"
          aria-orientation="vertical"
          aria-labelledby="dropdown-trigger"
          className="scrollbar-none tw-max-h-100 tw-absolute tw-right-0 tw-z-[999] tw-mt-2 tw-flex tw-min-w-80 tw-flex-col tw-items-stretch tw-justify-center tw-gap-4 tw-overflow-hidden tw-overflow-y-scroll tw-rounded-md tw-border tw-border-solid tw-border-gray-300 tw-bg-white tw-px-4 tw-pb-4 tw-pt-6 tw-shadow-xl child:tw-flex-grow"
        >
          <div className={`tw-flex tw-items-center tw-justify-between`}>
            <div
              className={`tw-mb-2 tw-whitespace-nowrap tw-px-0 tw-text-xl tw-font-semibold tw-text-textBlack`}
            >
              {translateCommon(CommonEnum['wishlist'])}
              <hr
                aria-hidden="true"
                className="tw-absolute tw-mt-1 tw-h-[3px] tw-w-[10%] tw-rounded-full tw-border-none tw-bg-custom-gold tw-opacity-100 max-nine:tw-h-[3px]"
              />
            </div>
            <DefaultLink
              className={`tw-text-[14px] tw-text-main`}
              href={Routes._DASHBOARD_WISHLIST}
            >
              {translateCommon(CommonEnum['view_all'])}
            </DefaultLink>
          </div>

          <div>
            {isLoadingWishlist ? (
              <HeaderWishlistDropdownSkeleton />
            ) : wishlist.skus.length === 0 ? (
              <NoDataCard type="wishlist" includeBorders={false} />
            ) : (
              <ul className="tw-mb-0 tw-w-full tw-list-none tw-space-y-2 tw-p-0">
                {[...wishlist.skus].slice(0, 5).map(sku => (
                  <li
                    key={sku.id}
                    role="none"
                    className="tw-group tw-relative tw-cursor-pointer tw-overflow-hidden tw-rounded-xl tw-border tw-border-solid tw-border-gray-100 tw-p-2 tw-transition-all tw-duration-300 hover:tw-scale-105 hover:tw-shadow-lg focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-200"
                  >
                    <DefaultLink
                      href={`/sku/${sku.slug}`}
                      role="menuitem"
                      className="tw-flex tw-items-center tw-gap-3 tw-rounded-md tw-py-2 tw-text-left tw-text-gray-700 tw-transition-colors"
                    >
                      <div className="tw-relative tw-w-16 tw-flex-shrink-0">
                        <SkuMainImage
                          src={getSkuMainImage(sku.mainImageUrl)}
                          alt={sku.name || 'Imagen del producto'}
                          width={64}
                          height={64}
                          className="tw-rounded-md tw-object-contain"
                        />
                      </div>

                      <div className="tw-flex tw-min-w-0 tw-flex-1 tw-flex-col tw-justify-between tw-pr-2">
                        <div className="tw-truncate tw-text-sm tw-font-semibold tw-text-gray-800">
                          {sku.name}
                        </div>
                        <p className="tw-m-0 tw-truncate tw-text-xs tw-text-gray-500">
                          {sku.brand?.name} • {sku.bottleSizeInMl}ml •{' '}
                          {sku.volumeInPercent ? `${sku.volumeInPercent / 10}%` : ''}
                        </p>
                      </div>

                      <div className="tw-flex tw-flex-col tw-items-end tw-gap-0.5">
                        <p className="tw-m-0 tw-flex tw-flex-col tw-p-0 tw-text-center tw-text-[12px] tw-font-medium tw-leading-normal tw-text-slate-gray">
                          {translateCommon(CommonEnum['lowest_ask'])}
                          <strong className="tw-text-[18px] tw-font-semibold tw-leading-normal tw-text-textBlack">
                            {sku.lowestAsk === 0 || !sku.lowestAsk
                              ? '--'
                              : toDe(sku.lowestAsk ?? 0)}
                          </strong>
                        </p>
                      </div>
                    </DefaultLink>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </Transition>
    </nav>
  );
}
